import { useState, useEffect, useRef } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Label } from '@/components/ui'
import { Palette, Copy, Check, RefreshCw, Upload, X, Crosshair } from 'lucide-react'
import { toast } from 'sonner'

interface ColorFormats {
  hex: string
  rgb: string
  hsl: string
  rgba: string
}

export function ColorPicker() {
  const [color, setColor] = useState('#80cbc4')
  const [alpha, setAlpha] = useState(100)
  const [copied, setCopied] = useState<string | null>(null)
  const [formats, setFormats] = useState<ColorFormats>({
    hex: '',
    rgb: '',
    hsl: '',
    rgba: '',
  })

  // Image picker state
  const [image, setImage] = useState<string | null>(null)
  const [isPickingFromImage, setIsPickingFromImage] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null
  }

  const rgbToHex = (r: number, g: number, b: number): string => {
    return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('')
  }

  const rgbToHsl = (r: number, g: number, b: number): { h: number; s: number; l: number } => {
    r /= 255
    g /= 255
    b /= 255

    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    let h = 0
    let s = 0
    const l = (max + min) / 2

    if (max !== min) {
      const d = max - min
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

      switch (max) {
        case r:
          h = ((g - b) / d + (g < b ? 6 : 0)) / 6
          break
        case g:
          h = ((b - r) / d + 2) / 6
          break
        case b:
          h = ((r - g) / d + 4) / 6
          break
      }
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100),
    }
  }

  useEffect(() => {
    const rgb = hexToRgb(color)
    if (!rgb) return

    const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b)
    const a = alpha / 100

    setFormats({
      hex: color.toUpperCase(),
      rgb: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`,
      hsl: `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`,
      rgba: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${a})`,
    })
  }, [color, alpha])

  const copyFormat = async (format: keyof ColorFormats) => {
    try {
      await navigator.clipboard.writeText(formats[format])
      setCopied(format)
      toast.success('Copied to clipboard')
      setTimeout(() => setCopied(null), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }

  const randomColor = () => {
    const hex = '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')
    setColor(hex)
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }

    const reader = new FileReader()
    reader.onload = (event) => {
      setImage(event.target?.result as string)
      setIsPickingFromImage(true)
    }
    reader.readAsDataURL(file)

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleImageClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height

    const x = Math.floor((e.clientX - rect.left) * scaleX)
    const y = Math.floor((e.clientY - rect.top) * scaleY)

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const pixel = ctx.getImageData(x, y, 1, 1).data
    const hex = rgbToHex(pixel[0], pixel[1], pixel[2])
    setColor(hex)
    toast.success(`Color picked: ${hex}`)
  }

  const handleImageLoad = () => {
    const canvas = canvasRef.current
    const img = imageRef.current
    if (!canvas || !img) return

    canvas.width = img.naturalWidth
    canvas.height = img.naturalHeight

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.drawImage(img, 0, 0)
  }

  const clearImage = () => {
    setImage(null)
    setIsPickingFromImage(false)
  }

  const presetColors = [
    '#80cbc4', '#ef4444', '#f97316', '#eab308', '#22c55e',
    '#3b82f6', '#8b5cf6', '#ec4899', '#ffffff', '#000000',
  ]

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <Palette className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Color Picker</h1>
            <p className="text-muted-foreground">Pick colors from image or convert between formats</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Color Picker */}
        <div className="space-y-6">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Pick Color</CardTitle>
              <CardDescription>Select a color using the picker or enter a value</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Color preview */}
              <div
                className="h-32 rounded-lg border border-border relative overflow-hidden"
                style={{ backgroundColor: color }}
              >
                <div
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)`,
                    backgroundSize: '20px 20px',
                    backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
                    opacity: 1 - alpha / 100,
                  }}
                />
              </div>

              {/* Color input */}
              <div className="flex gap-4">
                <div className="flex-1 space-y-2">
                  <Label>Hex Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      placeholder="#000000"
                      className="font-mono"
                    />
                  </div>
                </div>
                <Button variant="outline" size="icon" onClick={randomColor} className="mt-8">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>

              {/* Alpha slider */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label>Opacity</Label>
                  <span className="text-sm text-muted-foreground">{alpha}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={alpha}
                  onChange={(e) => setAlpha(Number(e.target.value))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                />
              </div>

              {/* Preset colors */}
              <div className="space-y-2">
                <Label>Presets</Label>
                <div className="flex flex-wrap gap-2">
                  {presetColors.map((preset) => (
                    <button
                      key={preset}
                      onClick={() => setColor(preset)}
                      className="w-8 h-8 rounded-lg border border-border hover:scale-110 transition-transform"
                      style={{ backgroundColor: preset }}
                      title={preset}
                    />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Image Color Picker */}
          <Card className="glass">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Pick from Image</CardTitle>
                  <CardDescription>Upload an image and click to pick colors</CardDescription>
                </div>
                {image && (
                  <Button variant="outline" size="sm" onClick={clearImage}>
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!image ? (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                >
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm font-medium">Click to upload image</p>
                  <p className="text-xs text-muted-foreground mt-1">PNG, JPG, WebP supported</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Crosshair className="h-4 w-4" />
                    <span>Click on the image to pick a color</span>
                  </div>
                  <div className="relative rounded-lg overflow-hidden border border-border">
                    <img
                      ref={imageRef}
                      src={image}
                      alt="Color source"
                      onLoad={handleImageLoad}
                      className="hidden"
                    />
                    <canvas
                      ref={canvasRef}
                      onClick={handleImageClick}
                      className="w-full h-auto cursor-crosshair max-h-[300px] object-contain"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Color Formats */}
        <Card className="glass h-fit">
          <CardHeader>
            <CardTitle>Color Formats</CardTitle>
            <CardDescription>Copy the color in different formats</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(Object.entries(formats) as [keyof ColorFormats, string][]).map(([format, value]) => (
              <div key={format} className="space-y-2">
                <Label className="uppercase text-xs">{format}</Label>
                <div className="flex gap-2">
                  <Input
                    value={value}
                    readOnly
                    className="font-mono"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyFormat(format)}
                  >
                    {copied === format ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            ))}

            {/* Color info */}
            <div className="pt-4 border-t border-border">
              <Label className="text-muted-foreground">Color Info</Label>
              <div className="mt-2 grid grid-cols-3 gap-4 text-center">
                <div className="p-3 rounded-lg bg-muted/30">
                  <p className="text-2xl font-bold text-foreground">
                    {hexToRgb(color)?.r || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Red</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30">
                  <p className="text-2xl font-bold text-foreground">
                    {hexToRgb(color)?.g || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Green</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30">
                  <p className="text-2xl font-bold text-foreground">
                    {hexToRgb(color)?.b || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Blue</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
